import { SiteDetailsSections } from "../../add-element/collapsed/CollapsedSiteDetails";
import { useNewMobileExperience } from "../../contexts/StaticSiteDataContext";
import { TRACKING } from "../../core/components/LaunchDarklyProvider";
import { useRouterNavigation } from "../../core/routing";
import { useScrollTo } from "../../hooks/useScrollTo";
import { buildRouteArgs } from "../../pages/utils";
import { Page, SectionTypes } from "../../resources/modelTypes";
import {
  sectionTypeToSwipePanelView,
  SwipePanelView,
} from "./online-invitation/mobile/SectionHeights";
import { SwipePanelRef } from "./online-invitation/mobile/SwipePanel";
import { useLDClient } from "launchdarkly-react-client-sdk";
import noop from "lodash/noop";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

export type ResourceEditMetadata =
  | {
      resourceType: "event" | "section";
      idOfResource: number;
    }
  | {
      resourceType: "siteDetails";
      idOfResource: SiteDetailsSections;
    };

export enum SidebarDisplayModes {
  OFF = "off",
  SM = "sm",
  LG = "lg",
}

export type FocusedClickSection = {
  id: number | SiteDetailsSections;
  isSolidBorder: boolean;
  type: "event" | "section" | "siteDetails";
};

export type HoveredSection = {
  id: number | SiteDetailsSections;
  type: "event" | "section" | "siteDetails";
};

export type SitePreviewContextType = {
  cancelPendingDiscard: () => void;
  discardChanges: () => void;
  focusedClickSection: FocusedClickSection | null;
  handleSidebarChange: (
    size: SidebarDisplayModes,
    showPreview?: boolean,
  ) => void;
  hasUnsavedChanges: boolean;
  hoveredSection: HoveredSection | null;
  isMobile: boolean;
  onSectionEditClick: (
    idOfResource: number | SiteDetailsSections,
    resourceType: "event" | "section" | "siteDetails",
    panelView: SwipePanelView,
    page?: Page,
  ) => void;
  pendingDiscardAction: (() => void) | null;
  resourceEditMetadata: ResourceEditMetadata | null;
  setFocusedClickSection: (section: FocusedClickSection | null) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  setHoveredSection: (section: HoveredSection | null) => void;
  setResourceEditMetadata: (metadata: ResourceEditMetadata | null) => void;
  setShouldAddNewSection: (value: boolean) => void;
  shouldAddNewSection: boolean;
  showMobilePreview: boolean;
  sidebarDisplayMode: SidebarDisplayModes;
  swipePanelRef: React.RefObject<SwipePanelRef>;
  triggerAddNewSection: (
    trackElement: (typeof TRACKING)[keyof typeof TRACKING],
    page?: Page,
    isWedding?: boolean,
  ) => void;
  runWithDiscardCheck: (action: () => void) => void;
  onSectionEditClickWithScroll: (
    idOfResource: number,
    resourceType: "event" | "section",
    sectionType: SectionTypes,
  ) => void;
  registerDiscardHandler: (handler: (() => void) | null) => void;
};

const SitePreviewContext = createContext<SitePreviewContextType>({
  cancelPendingDiscard: noop,
  discardChanges: noop,
  focusedClickSection: null,
  handleSidebarChange: noop,
  hasUnsavedChanges: false,
  hoveredSection: null,
  isMobile: false,
  onSectionEditClick: noop,
  pendingDiscardAction: null,
  resourceEditMetadata: null,
  setFocusedClickSection: noop,
  setHasUnsavedChanges: noop,
  setHoveredSection: noop,
  setResourceEditMetadata: noop,
  setShouldAddNewSection: noop,
  shouldAddNewSection: false,
  showMobilePreview: false,
  sidebarDisplayMode: SidebarDisplayModes.SM,
  swipePanelRef: {
    current: null,
  },
  triggerAddNewSection: noop,
  runWithDiscardCheck: noop,
  onSectionEditClickWithScroll: noop,
  registerDiscardHandler: noop,
});

type SitePreviewContextProviderProps = {
  children: ReactNode;
  isMobile: boolean;
};

export const SitePreviewContextProvider = ({
  children,
  isMobile,
}: SitePreviewContextProviderProps) => {
  const navigateTo = useRouterNavigation();
  const ldClient = useLDClient();
  const swipePanelRef = useRef<SwipePanelRef>(null);
  const useNewMobileExperienceFlag = useNewMobileExperience();
  const { scrollToId } = useScrollTo();

  const [resourceEditMetadata, setResourceEditMetadata] =
    useState<ResourceEditMetadata | null>(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Mirror hasUnsavedChanges in a ref so runWithDiscardCheck stays stable.
  // Inline update (not useEffect) ensures the ref is current before any event
  // handler fires within the same render cycle.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  const discardHandlerRef = useRef<(() => void) | null>(null);
  const registerDiscardHandler = useCallback((handler: (() => void) | null) => {
    discardHandlerRef.current = handler;
  }, []);
  const discardChanges = useCallback(() => {
    if (hasUnsavedChangesRef.current) {
      discardHandlerRef.current?.();
    }
  }, []);

  // Single pending-action slot shared by all guard sites (ManageSections back
  // arrow, SitePreviewContainerV2 navigation/slug guards).
  const [pendingDiscardAction, setPendingDiscardAction] = useState<
    (() => void) | null
  >(null);

  // If there are unsaved changes, store the action for deferred execution via
  // the UnsavedChangesModal; otherwise call action immediately.
  const runWithDiscardCheck = useCallback((action: () => void) => {
    if (hasUnsavedChangesRef.current) {
      // Updater form avoids the setState-with-function gotcha where React
      // treats a plain function argument as an updater rather than the value.
      setPendingDiscardAction(() => action);
    } else {
      action();
    }
  }, []); // stable — reads hasUnsavedChanges via ref

  const cancelPendingDiscard = useCallback(() => {
    setPendingDiscardAction(null);
  }, []);

  const [shouldAddNewSection, setShouldAddNewSection] =
    useState<boolean>(false);

  const [showMobilePreview, setShowMobilePreview] = useState<boolean>(false);

  const [sidebarDisplayMode, setSidebarDisplayMode] =
    useState<SidebarDisplayModes>(SidebarDisplayModes.SM);

  // Track focused section for two-stage click behavior
  const [focusedClickSection, setFocusedClickSection] =
    useState<FocusedClickSection | null>(null);

  // Track hovered section for desktop hover behavior
  const [hoveredSection, setHoveredSection] = useState<HoveredSection | null>(
    null,
  );

  // Clear focus when currently edited resource changes
  useEffect(() => {
    if (!focusedClickSection) {
      return;
    }

    // Clear focus state if no longer editing resource
    if (!resourceEditMetadata) {
      setFocusedClickSection(null);
      return;
    }

    // Clear focus state if user navigates to a different section
    const isDifferentSection =
      resourceEditMetadata.idOfResource !== focusedClickSection.id ||
      resourceEditMetadata.resourceType !== focusedClickSection.type;

    if (isDifferentSection) {
      setFocusedClickSection(null);
    }
    // Ignore focusedClickSection dependency since this only cares about resetting it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceEditMetadata]);

  // Reset unsaved changes flag when editing is closed (save or cancel path)
  useEffect(() => {
    if (!resourceEditMetadata) {
      setHasUnsavedChanges(false);
    }
  }, [resourceEditMetadata, setHasUnsavedChanges]);

  const handleSidebarChange = useCallback(
    (size: SidebarDisplayModes, showPreview: boolean = false) => {
      setShowMobilePreview(showPreview);
      setSidebarDisplayMode(size);
    },
    [],
  );

  const onSectionEditClick = useCallback(
    (
      idOfResource: number | SiteDetailsSections,
      resourceType: "event" | "section" | "siteDetails",
      panelView: SwipePanelView,
      page?: Page,
    ) => {
      // Sets current resource being edited in sidebar
      setResourceEditMetadata({
        idOfResource,
        resourceType,
      } as ResourceEditMetadata);

      if (isMobile) {
        // On mobile, show the sidebar and hide mobile preview when editing
        handleSidebarChange(SidebarDisplayModes.SM);

        // Transition to appropriate height based on panel view
        if (useNewMobileExperienceFlag) {
          navigateTo("pages");
          swipePanelRef.current?.transitionToPanelView(panelView);
        } else {
          swipePanelRef.current?.open(500);
        }
      }

      // Navigates to page and emits tracking event if clicking from site preview
      if (page) {
        const { name, params } = buildRouteArgs({
          data: page,
        });

        navigateTo(name, params);
      } else {
        ldClient?.track(TRACKING.PreviewSectionEditClicked);
      }
    },
    [
      navigateTo,
      ldClient,
      handleSidebarChange,
      isMobile,
      useNewMobileExperienceFlag,
      swipePanelRef,
    ],
  );

  const triggerAddNewSection = useCallback(
    (
      trackElement: (typeof TRACKING)[keyof typeof TRACKING],
      page?: Page,
      isWedding?: boolean,
    ) => {
      // For the new mobile experience, open the panel directly to the
      // section-type picker view so it's visible immediately.
      // For the old experience, open to a moderate height to show selection UI.
      if (useNewMobileExperienceFlag) {
        swipePanelRef.current?.transitionToPanelView(
          SwipePanelView.SelectSectionType,
        );
      } else {
        swipePanelRef.current?.open(500);
      }

      // Set flag to indicate we want to add a new section
      setResourceEditMetadata(null);
      setFocusedClickSection(null);
      setShouldAddNewSection(true);
      if (useNewMobileExperienceFlag) {
        navigateTo("pages");
      } else if (page) {
        // currently sent from a WW
        const { name, params } = buildRouteArgs({
          data: page,
        });
        navigateTo(name, params);
      }

      if (isMobile && isWedding) {
        // TO DO: let's remove this once we get mobile edition drawers on WW
        handleSidebarChange(SidebarDisplayModes.SM);
      }

      ldClient?.track(trackElement);
    },
    [
      ldClient,
      navigateTo,
      useNewMobileExperienceFlag,
      isMobile,
      handleSidebarChange,
    ],
  );

  const onSectionEditClickWithScroll = useCallback(
    (
      idOfResource: number,
      resourceType: "event" | "section",
      sectionType: SectionTypes,
    ) => {
      const panelView = sectionTypeToSwipePanelView(sectionType);
      onSectionEditClick(idOfResource, resourceType, panelView);
      scrollToId(`section-${idOfResource}`);
    },
    [onSectionEditClick, scrollToId],
  );

  const contextValue = useMemo<SitePreviewContextType>(
    () => ({
      cancelPendingDiscard,
      discardChanges,
      focusedClickSection,
      handleSidebarChange,
      hasUnsavedChanges,
      hoveredSection,
      isMobile,
      onSectionEditClick,
      pendingDiscardAction,
      resourceEditMetadata,
      setFocusedClickSection,
      setHasUnsavedChanges,
      setHoveredSection,
      setResourceEditMetadata,
      setShouldAddNewSection,
      shouldAddNewSection,
      showMobilePreview,
      sidebarDisplayMode,
      swipePanelRef,
      triggerAddNewSection,
      runWithDiscardCheck,
      onSectionEditClickWithScroll,
      registerDiscardHandler,
    }),
    [
      cancelPendingDiscard,
      discardChanges,
      focusedClickSection,
      handleSidebarChange,
      hasUnsavedChanges,
      hoveredSection,
      isMobile,
      onSectionEditClick,
      pendingDiscardAction,
      resourceEditMetadata,
      setFocusedClickSection,
      setHasUnsavedChanges,
      setHoveredSection,
      setResourceEditMetadata,
      setShouldAddNewSection,
      shouldAddNewSection,
      showMobilePreview,
      sidebarDisplayMode,
      swipePanelRef,
      triggerAddNewSection,
      runWithDiscardCheck,
      onSectionEditClickWithScroll,
      registerDiscardHandler,
    ],
  );

  return (
    <SitePreviewContext.Provider value={contextValue}>
      {children}
    </SitePreviewContext.Provider>
  );
};

export default SitePreviewContext;
